import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  Search,
  Waves,
  Wrench,
  Droplets,
  Hand,
  Wind,
  BadgeCheck,
  ShieldX,
  Building2,
  MapPin,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";

const SITE_URL = "https://deepcleanrr.com";
const CANONICAL = `${SITE_URL}/pricing`;

const LAYERS = [
  {
    icon: Search,
    n: "01",
    title: "Pre-Inspection & Treatment",
    desc: "We inspect problem areas including odor zones, buildup, stains, and high-traffic surfaces.",
  },
  {
    icon: Waves,
    n: "02",
    title: "Deep Foam Application",
    desc: "Specialized foam penetrates hard-to-reach areas and loosens grime, bacteria, and urine buildup.",
  },
  {
    icon: Wrench,
    n: "03",
    title: "Pressure Washing & Surface Cleaning",
    desc: "We thoroughly clean floors, partitions, walls, and surrounding surfaces for a complete restroom refresh.",
  },
  {
    icon: Droplets,
    n: "04",
    title: "Toilet & Urinal Sanitization",
    desc: "Every fixture receives targeted sanitation and odor-control treatment.",
  },
  {
    icon: Hand,
    n: "05",
    title: "High-Touch Point Disinfection",
    desc: "Handles, dispensers, sinks, countertops, and touchpoints disinfected to reduce germs and harmful bacteria.",
  },
  {
    icon: Wind,
    n: "06",
    title: "Odor Elimination",
    desc: "We neutralize restroom odors at the source instead of simply masking smells.",
  },
  {
    icon: BadgeCheck,
    n: "07",
    title: "Final Sanitation & Quality Check",
    desc: "Every restroom is inspected to ensure it meets our Deep Clean standard before service completion.",
  },
];

const NO_HIDDEN = [
  "No surprise service fees",
  "No unnecessary upsells",
  "No hidden sanitation charges",
  "Simple pricing based on restroom size and fixture count",
];

const AREAS = [
  "Wilmington",
  "Wrightsville Beach",
  "Carolina Beach",
  "Leland",
  "Castle Hayne",
  "Ogden",
];

const IDEAL_FOR = [
  "Restaurants",
  "Office buildings",
  "Retail stores",
  "Warehouses",
  "Gyms & fitness centers",
  "Medical facilities",
  "Schools",
  "Gas stations",
  "Apartment complexes",
  "High-traffic commercial properties",
];

const PricingPage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Commercial Restroom Deep Cleaning",
    provider: {
      "@type": "LocalBusiness",
      name: "Deep Clean Restroom Services",
      telephone: "+19102369971",
      email: "info@deepcleanrr.com",
      url: SITE_URL,
    },
    areaServed: AREAS.map((a) => ({ "@type": "City", name: a })),
    url: CANONICAL,
    description:
      "Transparent commercial restroom deep cleaning pricing — $39 per restroom plus $10 per toilet or urinal. 7 Layers of Clean. No hidden cost.",
    offers: [
      {
        "@type": "Offer",
        name: "Base Restroom Cleaning",
        price: "39",
        priceCurrency: "USD",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: 39,
          priceCurrency: "USD",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "restroom" },
        },
      },
      {
        "@type": "Offer",
        name: "Fixture Cleaning Add-On",
        price: "10",
        priceCurrency: "USD",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: 10,
          priceCurrency: "USD",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "toilet or urinal" },
        },
      },
    ],
  };

  return (
    <main data-testid="pricing-page" className="bg-white">
      <SEO
        title="Pricing | Commercial Restroom Deep Cleaning — Deep Clean"
        description="Transparent commercial restroom deep cleaning pricing — $39 per restroom + $10 per toilet or urinal. 7 Layers of Clean. No hidden cost. Cape Fear & Wrightsville Beach."
        canonical={CANONICAL}
        type="website"
        jsonLd={jsonLd}
        keywords={[
          "commercial restroom cleaning price Wilmington",
          "restroom deep cleaning cost",
          "Cape Fear sanitation pricing",
          "Wrightsville Beach restroom cleaning rates",
          "no hidden fee restroom cleaning",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-slate-950 pt-24">
        <div
          className="pointer-events-none absolute inset-0 -z-10"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 15%, rgba(6,182,212,0.28), transparent 70%), radial-gradient(55% 50% at 0% 100%, rgba(0,85,255,0.45), transparent 70%), linear-gradient(180deg, #0B1220 0%, #050913 100%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pb-20 pt-16 md:px-12 md:pb-28 md:pt-24">
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            — Pricing · Cape Fear & Wrightsville Beach
          </p>
          <h1
            data-testid="pricing-hero-heading"
            className="max-w-5xl font-heading text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-8xl"
          >
            7 layers of clean.
            <br />
            <span className="text-cyan-300">No hidden cost.</span>
          </h1>
          <p className="mt-8 max-w-3xl font-body text-base font-medium leading-relaxed text-slate-200 md:text-lg">
            Commercial restroom cleaning should be straightforward,
            professional, and transparent. Pricing built around how many
            restrooms you have and how many fixtures live inside them — nothing
            else.
          </p>
        </div>
      </section>

      {/* PRICING CARD */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mx-auto max-w-5xl">
            <div
              data-testid="pricing-card"
              className="relative overflow-hidden rounded-md border border-slate-200 bg-white shadow-xl shadow-slate-900/5"
            >
              {/* Top label strip */}
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4 md:px-10">
                <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
                  Deep Clean Pricing
                </p>
                <p className="inline-flex items-center gap-1.5 font-body text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  <BadgeCheck size={14} strokeWidth={2.2} className="text-blue-600" />
                  Transparent · Per-unit
                </p>
              </div>

              <div className="grid grid-cols-1 divide-y divide-slate-200 md:grid-cols-2 md:divide-x md:divide-y-0">
                {/* Base */}
                <div className="p-8 md:p-12">
                  <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
                    Base Restroom Cleaning
                  </p>
                  <div className="mt-5 flex items-end gap-1.5">
                    <span
                      data-testid="price-base"
                      className="font-heading text-7xl font-black leading-none tracking-tighter text-slate-900 md:text-8xl"
                    >
                      $39
                    </span>
                    <span className="mb-3 font-body text-sm font-bold uppercase tracking-wider text-slate-500">
                      / restroom
                    </span>
                  </div>
                  <p className="mt-5 font-body text-base font-medium leading-relaxed text-slate-600">
                    Covers the complete <span className="font-bold text-slate-900">7 Layers of Clean</span> program for one
                    restroom — sanitization, foam treatment, pressure washing,
                    odor elimination, and high-touch disinfection.
                  </p>
                </div>

                {/* Fixture */}
                <div className="bg-slate-50 p-8 md:p-12">
                  <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
                    Fixture Cleaning · Add-On
                  </p>
                  <div className="mt-5 flex items-end gap-1.5">
                    <span className="font-heading text-7xl font-black leading-none tracking-tighter text-blue-600 md:text-8xl">
                      +$10
                    </span>
                    <span className="mb-3 font-body text-sm font-bold uppercase tracking-wider text-slate-500">
                      / toilet or urinal
                    </span>
                  </div>
                  <p className="mt-5 font-body text-base font-medium leading-relaxed text-slate-600">
                    Targeted sanitization and descaling of every toilet and
                    urinal — billed per fixture so you only pay for what's
                    actually in your restrooms.
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-3 border-t border-slate-200 bg-white p-6 md:flex-row md:items-center md:justify-between md:p-8">
                <p className="font-body text-sm font-medium text-slate-600">
                  Want a real number for your facility? We'll send a custom
                  quote within one business day.
                </p>
                <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                  <Link
                    to="/#book"
                    data-testid="pricing-book-btn"
                    className="group inline-flex h-12 items-center justify-center gap-2 rounded-md bg-blue-600 px-6 font-body text-sm font-bold text-white hover:bg-blue-700"
                  >
                    Get a Free Quote
                    <ArrowRight size={14} strokeWidth={2.4} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <a
                    href="tel:9102369971"
                    data-testid="pricing-call-btn"
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-md border border-slate-300 px-6 font-body text-sm font-bold text-slate-900 hover:border-blue-600 hover:text-blue-600"
                  >
                    <Phone size={14} strokeWidth={2.4} />
                    Call 910-236-9971
                  </a>
                </div>
              </div>
            </div>

            {/* Example calculator */}
            <div
              data-testid="pricing-example"
              className="mt-8 rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 md:p-8"
            >
              <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
                Quick example
              </p>
              <p className="mt-3 font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
                2 restrooms · 4 toilets · 2 urinals = <span className="text-blue-600">$138</span>
              </p>
              <p className="mt-2 font-body text-sm font-medium text-slate-600">
                (2 × $39) + (6 × $10) = $138 total. Final pricing varies with
                facility traffic and condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7 LAYERS */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — What's Included
              </p>
              <h2
                data-testid="pricing-layers-heading"
                className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
              >
                7 Layers of Clean.
                <br />
                <span className="text-blue-600">In every single visit.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                Every restroom we clean moves through the same seven-step
                protocol. No tier upgrades. No add-ons hiding behind a paywall.
              </p>
            </div>
          </div>

          <ol
            data-testid="pricing-layers-list"
            className="overflow-hidden rounded-md border border-slate-200 bg-white"
          >
            {LAYERS.map((s, i) => (
              <li
                key={s.title}
                data-testid={`pricing-layer-${i}`}
                className="group grid grid-cols-[auto,auto,1fr,auto] items-start gap-5 border-b border-slate-200 p-6 transition-colors last:border-b-0 hover:bg-slate-50 md:p-8"
              >
                <span className="mt-1 font-heading text-sm font-bold tabular-nums text-slate-400">
                  {s.n}
                </span>
                <span className="grid h-11 w-11 place-items-center rounded-md border border-slate-200 bg-white text-blue-600 transition-colors group-hover:border-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                  <s.icon size={20} strokeWidth={1.6} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 md:text-xl">
                    {s.title}
                  </h3>
                  <p className="mt-1.5 font-body text-sm font-medium leading-relaxed text-slate-600">
                    {s.desc}
                  </p>
                </div>
                <span className="mt-1 inline-flex h-6 items-center gap-1 rounded-full bg-blue-50 px-2.5 font-body text-[10px] font-bold uppercase tracking-wider text-blue-700">
                  <CheckCircle2 size={12} strokeWidth={3} />
                  Included
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* NO HIDDEN COSTS */}
      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-5">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — No Hidden Costs
              </p>
              <h2
                data-testid="pricing-honest-heading"
                className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
              >
                What you see is what you pay.
              </h2>
              <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                Most cleaning companies bury fees, surge charges, and
                "specialty surface" upsells in the fine print. We don't. Two
                line items, one invoice.
              </p>
            </div>

            <div className="md:col-span-7">
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {NO_HIDDEN.map((n, i) => (
                  <li
                    key={n}
                    data-testid={`no-hidden-${i}`}
                    className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-5"
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600">
                      <ShieldX size={16} strokeWidth={2.2} />
                    </span>
                    <span className="font-body text-sm font-bold leading-snug text-slate-800 md:text-base">
                      {n}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AREAS + IDEAL FOR */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Service Areas
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Cape Fear &amp; Wrightsville Beach.
            </h2>
            <p className="mt-4 font-body text-base font-medium leading-relaxed text-slate-600">
              Same flat pricing across every market we serve.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {AREAS.map((a, i) => (
                <div
                  key={a}
                  data-testid={`pricing-area-${i}`}
                  className="group flex items-center gap-2.5 rounded-md border border-slate-200 bg-white p-3 transition-colors hover:border-blue-600"
                >
                  <MapPin size={14} strokeWidth={2} className="shrink-0 text-blue-600" />
                  <span className="font-body text-[13px] font-semibold text-slate-800">
                    {a}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Ideal For
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Built for high-traffic businesses.
            </h2>
            <p className="mt-4 font-body text-base font-medium leading-relaxed text-slate-600">
              From neighborhood restaurants to industrial properties, our
              program scales to fit your facility.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {IDEAL_FOR.map((ind, i) => (
                <div
                  key={ind}
                  data-testid={`pricing-ideal-${i}`}
                  className="group flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3.5 transition-colors hover:border-blue-600"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                    <Building2 size={14} strokeWidth={2} />
                  </span>
                  <span className="font-body text-sm font-semibold text-slate-800">
                    {ind}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-slate-950 py-20 text-white md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.40), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:px-12 lg:flex-row lg:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Get a Free Quote
            </p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl lg:text-5xl">
              Ready for a real number — without the runaround?
            </h2>
            <p className="mt-4 max-w-xl font-body text-sm font-medium text-slate-300 md:text-base">
              7 Layers of Clean. No Hidden Cost.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="pricing-cta-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Get a Free Quote
              <ArrowRight size={18} strokeWidth={2.2} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="pricing-cta-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              910-236-9971
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default PricingPage;
